import { X } from "lucide-react";
import { motion, useDragControls } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";

interface FloatingWindowProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  defaultPosition?: { x: number; y: number };
  defaultSize?: { width: number; height: number };
  minSize?: { width: number; height: number };
  maxSize?: { width: number; height: number };
}

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < breakpoint,
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

const TITLEBAR_H = 40;
const EDGE = 12;

/**
 * A small, focused, macOS-style floating app window. Opens at a comfortable
 * size (never huge), clamps to the viewport, floats above the chat, and is
 * draggable + resizable with the content reflowing at any size. On phones it
 * becomes a bottom sheet. One task per window.
 */
export function FloatingWindow({
  title,
  onClose,
  children,
  defaultPosition,
  defaultSize = { width: 480, height: 560 },
  minSize = { width: 320, height: 360 },
  maxSize = { width: 760, height: 10000 },
}: FloatingWindowProps) {
  const isMobile = useIsMobile();
  const dragControls = useDragControls();

  const bounds = useCallback(() => {
    const maxW = Math.min(maxSize.width, window.innerWidth - EDGE * 2);
    const maxH = Math.min(maxSize.height, window.innerHeight - 96);
    return { maxW, maxH };
  }, [maxSize.width, maxSize.height]);

  const [size, setSize] = useState(() => {
    if (typeof window === "undefined") return defaultSize;
    const { maxW, maxH } = bounds();
    return {
      width: clamp(defaultSize.width, minSize.width, maxW),
      height: clamp(defaultSize.height, minSize.height, maxH),
    };
  });

  const [position, setPosition] = useState(() => {
    if (typeof window === "undefined") return defaultPosition ?? { x: 0, y: 0 };
    const w = clamp(defaultSize.width, minSize.width, bounds().maxW);
    const h = clamp(defaultSize.height, minSize.height, bounds().maxH);
    const start =
      defaultPosition ?? {
        // Sit center-right so the chat panel on the left stays usable.
        x: Math.round(window.innerWidth * 0.56 - w / 2),
        y: Math.round(window.innerHeight / 2 - h / 2),
      };
    return {
      x: clamp(start.x, EDGE, Math.max(EDGE, window.innerWidth - w - EDGE)),
      y: clamp(start.y, EDGE, Math.max(EDGE, window.innerHeight - h - EDGE)),
    };
  });

  const dragRef = useRef<{ px: number; py: number; ox: number; oy: number } | null>(null);
  const resizeRef = useRef<{ px: number; py: number; ow: number; oh: number } | null>(null);

  // Keep the window on-screen when the browser resizes.
  useEffect(() => {
    if (isMobile) return;
    const onResize = () => {
      const { maxW, maxH } = bounds();
      setSize((s) => ({
        width: clamp(s.width, minSize.width, maxW),
        height: clamp(s.height, minSize.height, maxH),
      }));
      setPosition((p) => ({
        x: clamp(p.x, EDGE, Math.max(EDGE, window.innerWidth - minSize.width - EDGE)),
        y: clamp(p.y, EDGE, Math.max(EDGE, window.innerHeight - TITLEBAR_H - EDGE)),
      }));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isMobile, bounds, minSize.width, minSize.height]);

  const onDragStart = useCallback(
    (e: React.PointerEvent) => {
      if (isMobile) return;
      e.preventDefault();
      e.stopPropagation();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      dragRef.current = { px: e.clientX, py: e.clientY, ox: position.x, oy: position.y };
    },
    [isMobile, position],
  );

  const onDragMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const w = document.body.clientWidth;
    const el = (e.currentTarget as HTMLElement).closest("[data-floating-window]") as HTMLElement | null;
    const winW = el?.offsetWidth ?? 0;
    const winH = el?.offsetHeight ?? 0;
    const nx = dragRef.current.ox + (e.clientX - dragRef.current.px);
    const ny = dragRef.current.oy + (e.clientY - dragRef.current.py);
    setPosition({
      x: clamp(nx, EDGE, Math.max(EDGE, w - winW - EDGE)),
      y: clamp(ny, EDGE, Math.max(EDGE, window.innerHeight - winH - EDGE)),
    });
  }, []);

  const onDragEnd = useCallback(() => {
    dragRef.current = null;
  }, []);

  const onResizeStart = useCallback(
    (e: React.PointerEvent) => {
      if (isMobile) return;
      e.preventDefault();
      e.stopPropagation();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      resizeRef.current = { px: e.clientX, py: e.clientY, ow: size.width, oh: size.height };
    },
    [isMobile, size],
  );

  const onResizeMove = useCallback(
    (e: React.PointerEvent) => {
      if (!resizeRef.current) return;
      const { maxW, maxH } = bounds();
      setSize({
        width: clamp(resizeRef.current.ow + (e.clientX - resizeRef.current.px), minSize.width, maxW),
        height: clamp(resizeRef.current.oh + (e.clientY - resizeRef.current.py), minSize.height, maxH),
      });
    },
    [bounds, minSize.width, minSize.height],
  );

  const onResizeEnd = useCallback(() => {
    resizeRef.current = null;
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const trafficLights = (
    <div className="flex shrink-0 items-center gap-2">
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        type="button"
        aria-label="Close"
        className="group flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[#FF5F57] transition-transform hover:scale-110"
      >
        <X className="h-2 w-2 text-black/50 opacity-0 transition-opacity group-hover:opacity-100" strokeWidth={4} />
      </button>
      <span className="h-3.5 w-3.5 rounded-full bg-[#FEBC2E]" aria-hidden="true" />
      <span className="h-3.5 w-3.5 rounded-full bg-[#28C840]" aria-hidden="true" />
    </div>
  );

  const titleLabel = (
    <span className="pointer-events-none flex-1 truncate text-center font-body text-[11px] font-bold tracking-wider text-home-ink/60 uppercase dark:text-white/60">
      {title}
    </span>
  );

  // ---- Mobile: bottom sheet ----
  // Swipe down on the grabber/header to dismiss (drag is limited to the header
  // so the content still scrolls). A large close button is the tap fallback.
  if (isMobile) {
    return (
      <motion.div
        drag="y"
        dragControls={dragControls}
        dragListener={false}
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={{ top: 0, bottom: 0.6 }}
        onDragEnd={(_, info) => {
          if (info.offset.y > 120 || info.velocity.y > 600) onClose();
        }}
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 420, damping: 38 }}
        className="pointer-events-auto fixed inset-x-0 bottom-0 z-40 flex max-h-[88vh] flex-col overflow-hidden rounded-t-3xl border-t border-home-ink/10 bg-home-cream text-home-ink shadow-[0_-16px_50px_-12px_rgba(33,31,28,0.4)] dark:border-white/10 dark:bg-neutral-950 dark:text-white"
      >
        <div
          onPointerDown={(e) => dragControls.start(e)}
          className="shrink-0 cursor-grab touch-none select-none border-b border-home-ink/8 active:cursor-grabbing dark:border-white/10"
        >
          <div className="mx-auto mt-2.5 h-1.5 w-10 rounded-full bg-home-ink/15 dark:bg-white/25" />
          <div className="flex items-center gap-3 px-4 py-2.5">
            <span className="min-w-0 flex-1 truncate font-body text-[14px] font-bold tracking-wide text-home-ink/75 dark:text-white/75">
              {title}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              onPointerDown={(e) => e.stopPropagation()}
              type="button"
              aria-label="Close"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-home-ink/[0.06] text-home-ink/70 transition-transform active:scale-90 dark:bg-white/10 dark:text-white/70"
            >
              <X className="h-5 w-5" strokeWidth={2.4} />
            </button>
          </div>
        </div>
        <div className="min-w-0 flex-1 overflow-auto overscroll-contain p-4">{children}</div>
      </motion.div>
    );
  }

  // ---- Desktop: floating window ----
  return (
    <div
      data-floating-window
      className="pointer-events-auto absolute z-40 flex flex-col overflow-hidden rounded-2xl border border-home-ink/10 bg-home-cream text-home-ink shadow-[0_24px_60px_-18px_rgba(33,31,28,0.45)] dark:border-white/10 dark:bg-neutral-950 dark:text-white"
      style={{ left: position.x, top: position.y, width: size.width, height: size.height }}
    >
      <div
        className="flex h-10 shrink-0 cursor-grab items-center gap-3 border-b border-home-ink/8 px-3.5 active:cursor-grabbing dark:border-white/10"
        onPointerDown={onDragStart}
        onPointerMove={onDragMove}
        onPointerUp={onDragEnd}
      >
        {trafficLights}
        {titleLabel}
        <span className="w-[52px] shrink-0" aria-hidden="true" />
      </div>

      <div className="min-w-0 flex-1 overflow-auto p-4">{children}</div>

      {/* Resize handle */}
      <div
        className="absolute right-0 bottom-0 h-5 w-5 cursor-nwse-resize"
        onPointerDown={onResizeStart}
        onPointerMove={onResizeMove}
        onPointerUp={onResizeEnd}
      >
        <svg viewBox="0 0 16 16" className="h-full w-full p-1 text-home-ink/25 dark:text-white/25" fill="currentColor">
          <circle cx="12" cy="12" r="1.4" />
          <circle cx="8" cy="12" r="1.4" />
          <circle cx="12" cy="8" r="1.4" />
        </svg>
      </div>
    </div>
  );
}
